import React, { type ComponentType, type ReactNode } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ShieldIcon from '@mui/icons-material/Shield';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { isEmptyString } from 'utils/helper';

export type EnrollmentIcon = ComponentType<SvgIconProps>;

type EnrollmentParagraphSection = {
  type: 'paragraph';
  content: ReactNode;
};

type EnrollmentFeatureListSection = {
  type: 'feature-list';
  items: Array<{
    icon: EnrollmentIcon;
    text: ReactNode;
  }>;
};

type EnrollmentInfoRowsSection = {
  type: 'info-rows';
  items: Array<{
    icon: EnrollmentIcon;
    label: ReactNode;
    description: ReactNode;
  }>;
};

type EnrollmentCardsSection = {
  type: 'cards';
  items: Array<{
    icon: EnrollmentIcon;
    title: ReactNode;
    description: ReactNode;
    tone?: 'brand' | 'warning' | 'danger' | 'neutral';
  }>;
};

type EnrollmentCalloutSection = {
  type: 'callout';
  content: ReactNode;
};

export type EnrollmentSection =
  | EnrollmentParagraphSection
  | EnrollmentFeatureListSection
  | EnrollmentInfoRowsSection
  | EnrollmentCardsSection
  | EnrollmentCalloutSection;

export type EnrollmentStepConfig = {
  id: string;
  title: string;
  lead: ReactNode;
  icon: EnrollmentIcon;
  sections: EnrollmentSection[];
};

type EnrollmentStepsParams = {
  patientName?: string | null;
  providerName?: string | null;
  organizationName?: string | null;
};

export function getEnrollmentSteps({
  patientName,
  providerName,
  organizationName,
}: EnrollmentStepsParams): EnrollmentStepConfig[] {
  const displayProviderName = providerName || 'Your provider';
  const displayPatientName = patientName || 'you';
  const careTeamLabel = !isEmptyString(organizationName)
    ? `your care team at ${organizationName}`
    : 'your care team';

  return [
    {
      id: 'program-welcome',
      icon: AutoAwesomeIcon,
      title: 'Welcome to the Populate Treatment Progress Program',
      lead: (
        <>
          <strong>{displayProviderName}</strong>
          {' '}
          recommended you,
          {' '}
          <strong>{displayPatientName}</strong>
          , for the Treatment Progress Monitoring program to monitor your health condition.
        </>
      ),
      sections: [
        {
          type: 'callout',
          content: (
            <>
              Short, regular check-ins give your care team a clearer picture of
              how you&apos;re doing
              between visits — so they can adjust your plan when something changes.
            </>
          ),
        },
      ],
    },
    {
      id: 'what-is-rtm',
      icon: FavoriteIcon,
      title: 'What is Treatment Progress Monitoring?',
      lead: (
        <>
          Treatment Progress Monitoring is a way for your care team to follow your recovery
          between visits using short check-ins you complete from your phone.
        </>
      ),
      sections: [
        {
          type: 'feature-list',
          items: [
            {
              icon: ChatBubbleOutlineIcon,
              text: 'You share brief updates about pain, function, and your care plan',
            },
            {
              icon: TrendingUpIcon,
              text: 'Your care team sees trends over time — not just visit-day snapshots',
            },
            {
              icon: FavoriteIcon,
              text: "Your providers can step in earlier if your responses suggest something's off",
            },
          ],
        },
      ],
    },
    {
      id: 'weekly-check-in',
      icon: AssignmentIcon,
      title: "What you'll do",
      lead: (
        <>
          Every two weeks, you&apos;ll get email & sms with a link to
          a short check-in in your patient portal.
          Most check-ins take about a minute. For your pain management plan, we&apos;ll ask about:
        </>
      ),
      sections: [
        {
          type: 'feature-list',
          items: [
            {
              icon: TimelineIcon,
              text: 'Pain level over the past week',
            },
            {
              icon: TimelineIcon,
              text: 'How pain affects daily activities and sleep',
            },
            {
              icon: TimelineIcon,
              text: 'Movement, function, and flare-ups',
            },
            {
              icon: TimelineIcon,
              text: 'Medication and home program adherence',
            },
          ],
        },
        {
          type: 'callout',
          content: (
            <>
              There are no right or wrong answers. Just tell us how you&apos;ve actually been doing.
            </>
          ),
        },
      ],
    },
    {
      id: 'how-responses-are-used',
      icon: TrendingUpIcon,
      title: 'How your responses are used',
      lead: (
        <>
          Your check-ins become part of your medical record and are reviewed by
          {' '}
          {careTeamLabel}
          {' '}
          to support your chronic lower-back pain management.
        </>
      ),
      sections: [
        {
          type: 'info-rows',
          items: [
            {
              icon: TrendingUpIcon,
              label: 'Tracking progress',
              description: "Your care team looks at trends to see what's working and what isn't.",
            },
            {
              icon: ChatBubbleOutlineIcon,
              label: 'Guiding outreach',
              description: 'If something stands out, your team may reach out by phone or message.',
            },
            {
              icon: ShieldIcon,
              label: 'Privacy',
              description:
                'Your responses are shared only with the providers treating you, the same as any other part of your chart.',
            },
          ],
        },
        {
          type: 'callout',
          content: (
            <>
              <strong>What Treatment Progress Monitoring is not:</strong>
              {' '}
              <br />
              {' '}
              check-ins aren&apos;t monitored in real time
              and aren&apos;t a substitute for a visit, a phone call, or emergency care.
            </>
          ),
        },
      ],
    },
    {
      id: 'safety-guidance',
      icon: WarningAmberRoundedIcon,
      title: 'Safety & what to do if something feels wrong',
      lead: (
        <>
          Care Progress is designed for routine updates between visits. It&apos;s important to know
          when to use it — and when to reach out another way.
        </>
      ),
      sections: [
        {
          type: 'cards',
          items: [
            {
              icon: WarningAmberRoundedIcon,
              title: 'For emergencies, call 911',
              description:
                'Chest pain, trouble breathing, severe bleeding, sudden weakness, or any life-threatening symptom — call 911 or go to the nearest emergency department.',
              tone: 'danger',
            },
            {
              icon: PhoneIcon,
              title: 'For urgent concerns, call the office',
              description:
                "A new injury, a sudden change in your condition, or something that can't wait until your next visit — call us directly. Don't wait for your next visit.",
              tone: 'warning',
            },
            {
              icon: AccessTimeIcon,
              title: 'Check-ins are not monitored 24/7',
              description:
                'Your responses are reviewed during business hours.',
              tone: 'neutral',
            },
          ],
        },
      ],
    },
  ];
}
